/**
 * Insights Message Component
 *
 * Displays individual chat messages with founder-focused styling.
 */

import React, { memo } from 'react';
import { View, Text } from 'react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { Lightbulb, User, CheckCircle } from 'lucide-react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';
import { formatMessageTime } from '@/lib/format';
import type { InsightsMessageProps } from '@/lib/startup-insights/types';

function InsightsMessageComponent({
  message,
  isLast = false,
  isStreaming = false,
  actionItems = [],
  onSuggestionTap,
}: InsightsMessageProps) {
  const colors = useThemeColors();
  const isAssistant = message.role === 'assistant';
  const isUser = message.role === 'user';

  const formattedTime = formatMessageTime(message.timestamp);

  return (
    <Animated.View
      entering={FadeInUp.duration(300)}
      className={cn(
        'mb-4',
        isUser && 'items-end',
        isAssistant && 'items-start'
      )}
    >
      {/* Message container */}
      <View
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-3',
          isUser && 'rounded-br-md',
          isAssistant && 'rounded-bl-md'
        )}
        style={{ backgroundColor: isUser ? colors.primary[600] : colors.surface.tertiary }}
      >
        {/* Header with role icon */}
        <View className="flex-row items-center mb-2">
          <View
            className="w-6 h-6 rounded-full items-center justify-center mr-2 shrink-0"
            style={{ backgroundColor: isUser ? colors.surface.primary : colors.amber.light }}
          >
            {isAssistant
              ? <Lightbulb size={14} color={colors.amber.main} />
              : <User size={14} color={colors.primary[600]} />}
          </View>
          <Text
            className="text-xs flex-1"
            numberOfLines={1}
            style={{ color: isUser ? colors.text.inverse : colors.text.tertiary }}
          >
            {isAssistant ? 'AI Advisor' : 'You'} · {formattedTime}
          </Text>
        </View>

        {/* Message content */}
        <MessageContent
          content={message.content}
          isStreaming={isStreaming && isLast}
          onSurface={isUser ? colors.text.inverse : colors.text.primary}
        />

        {/* Action items indicator */}
        {isAssistant && actionItems.length > 0 && (
          <View className="mt-3 pt-2 border-t" style={{ borderTopColor: colors.border.light }}>
            <View className="flex-row items-center gap-1 mb-1">
              <CheckCircle size={12} color={colors.traffic.green} />
              <Text className="text-xs font-medium" style={{ color: colors.traffic.green }}>
                {actionItems.length} action item{actionItems.length > 1 ? 's' : ''} identified
              </Text>
            </View>
          </View>
        )}

        {/* Metadata (for assistant messages) */}
        {isAssistant && message.metadata && (
          <View className="mt-2 pt-2 border-t" style={{ borderTopColor: colors.border.light }}>
            <View className="flex-row items-center gap-3">
              {message.metadata.processingTime && (
                <Text className="text-xs" style={{ color: colors.text.tertiary }}>
                  {(message.metadata.processingTime / 1000).toFixed(1)}s
                </Text>
              )}
              {message.metadata.topics && message.metadata.topics.length > 0 && (
                <View className="flex-row gap-1">
                  {message.metadata.topics.map((topic) => (
                    <View
                      key={topic}
                      className="px-2 py-0.5 rounded"
                      style={{ backgroundColor: colors.surface.primary }}
                    >
                      <Text className="text-[10px] capitalize" numberOfLines={1} style={{ color: colors.text.secondary }}>
                        {topic}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        )}
      </View>
    </Animated.View>
  );
}

/**
 * Message content with markdown-like formatting
 */
function MessageContent({
  content,
  isStreaming,
  onSurface,
}: {
  content: string;
  isStreaming: boolean;
  /** Readable text colour for the bubble this content sits on. The two roles
   *  have different backgrounds, so one hardcoded colour cannot serve both. */
  onSurface: string;
}) {
  const colors = useThemeColors();
  // Split content into sections
  const sections = content.split('\n\n');

  return (
    <View>
      {sections.map((section, index) => (
        <View key={index} className={index > 0 ? 'mt-3' : ''}>
          {renderSection(section, onSurface, colors)}
        </View>
      ))}
      {isStreaming && (
        <View className="mt-2">
          <StreamingIndicator />
        </View>
      )}
    </View>
  );
}

/**
 * Render a section of content with basic markdown support
 */
function renderSection(
  section: string,
  onSurface: string,
  colors: ReturnType<typeof useThemeColors>,
) {
  const lines = section.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) continue;

    // Headers (## )
    if (trimmed.startsWith('## ')) {
      elements.push(
        <Text key={i} className="text-base font-semibold mb-2" style={{ color: onSurface }}>
          {trimmed.replace('## ', '')}
        </Text>
      );
    }
    // Bold headers (**text**)
    else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      elements.push(
        <Text key={i} className="text-sm font-medium mt-2 mb-1" style={{ color: onSurface }}>
          {trimmed.replace(/\*\*/g, '')}
        </Text>
      );
    }
    // Status indicators (✓, ⚠, ✗)
    else if (trimmed.startsWith('✓') || trimmed.startsWith('✗') || trimmed.startsWith('⚠️')) {
      const isPositive = trimmed.startsWith('✓');
      const isNegative = trimmed.startsWith('✗');
      elements.push(
        <View key={i} className="flex-row mt-1">
          <Text
            className="mr-2"
            style={{ color: isPositive ? colors.traffic.green : isNegative ? colors.traffic.red : colors.amber.main }}
          >
            {trimmed.charAt(0)}
          </Text>
          <Text className="text-sm flex-1" style={{ color: onSurface }}>
            {formatInlineText(trimmed.substring(2), onSurface)}
          </Text>
        </View>
      );
    }
    // Bullet points (- or •)
    else if (trimmed.startsWith('-') || trimmed.startsWith('•')) {
      const bulletText = trimmed.replace(/^[-•]\s*/, '');
      elements.push(
        <View key={i} className="flex-row mt-1">
          <Text className="mr-2" style={{ color: onSurface }}>•</Text>
          <Text className="text-sm flex-1" style={{ color: onSurface }}>
            {formatInlineText(bulletText, onSurface)}
          </Text>
        </View>
      );
    }
    // Numbered lists
    else if (trimmed.match(/^\d+\./)) {
      const [num, ...rest] = trimmed.split('.');
      const listText = rest.join('.').trim();
      elements.push(
        <View key={i} className="flex-row mt-1">
          <Text className="mr-2 w-5 font-medium" style={{ color: colors.amber.main }}>{num}.</Text>
          <Text className="text-sm flex-1" style={{ color: onSurface }}>
            {formatInlineText(listText, onSurface)}
          </Text>
        </View>
      );
    }
    // Arrow indicators (→)
    else if (trimmed.startsWith('→')) {
      elements.push(
        <View key={i} className="flex-row mt-1 ml-4">
          <Text className="mr-2" style={{ color: colors.amber.main }}>→</Text>
          <Text className="text-sm flex-1 italic" style={{ color: colors.amber.main }}>
            {formatInlineText(trimmed.substring(1).trim(), onSurface)}
          </Text>
        </View>
      );
    }
    // Regular paragraph
    else {
      elements.push(
        <Text key={i} className="text-sm leading-relaxed" style={{ color: onSurface }}>
          {formatInlineText(trimmed, onSurface)}
        </Text>
      );
    }
  }

  return <>{elements}</>;
}

/**
 * Format inline text with bold support
 */
function formatInlineText(text: string, onSurface: string): React.ReactNode {
  // Match **bold** text
  const parts = text.split(/(\*\*[^*]+\*\*)/);

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Text key={i} className="font-medium" style={{ color: onSurface }}>
          {part.replace(/\*\*/g, '')}
        </Text>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/**
 * Streaming indicator animation
 */
function StreamingIndicator() {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-center gap-1">
      <View className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: colors.amber.main }} />
      <View className="w-2 h-2 rounded-full animate-pulse delay-100" style={{ backgroundColor: colors.amber.main }} />
      <View className="w-2 h-2 rounded-full animate-pulse delay-200" style={{ backgroundColor: colors.amber.main }} />
    </View>
  );
}

export const InsightsMessage = memo(InsightsMessageComponent);
